import { Pencil, TrashIcon } from 'assets/icons';
import AssignMenu from 'components/assign/AssignMenu';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import CommentComposer from 'components/comments/CommentComposer';
import CommentThread from 'components/comments/CommentThread';
import useCommentThread from 'components/comments/useCommentThread';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import LineTabs from 'components/tabs/LineTabs';
import UndoToast from 'components/toasts/UndoToast';
import Tooltips from 'components/tooltips/Tooltips';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useTaskPanels from 'hooks/useTaskPanels';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import TaskActivityTab from './TaskActivityTab';
import TaskDetailTab from './TaskDetailTab';
import { TaskActivitySkeleton, TaskCommentsSkeleton, TaskDetailSkeleton } from './TaskPanelSkeletons';

/**
 * The panel of a task (slide-over `get_task`, its id in `objID`): its detail,
 * its activity and its comments, the colleague it is assigned to, and what
 * edits, deletes, ticks or carries it out (call, send the e-mail). Every
 * change tells the lists (useTasksChanged).
 */
const TaskPanel = () => {
  const { objID, setObjID } = UseContext();
  // The task's id, or { taskId, tab } to open it on another tab (useTaskPanels).
  const taskId = typeof objID === 'string' ? objID : objID?.taskId ?? null;
  const initialTab = (typeof objID === 'object' && objID?.tab) || 'detail';
  const { text } = useText('taskPanel');
  const { text: tasksText } = useText('tasks');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const { openTaskForm } = useTaskPanels();
  const notify = useNotifyTasksChanged();
  const [task, setTask] = useState(null);
  const [failed, setFailed] = useState(false);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState('detail');

  const load = useCallback(
    () => call(crmApi.getTask, { id: taskId }).then(setTask, () => setFailed(true)),
    [call, taskId],
  );
  useEffect(() => {
    if (!taskId || !me) return;
    setTask(null);
    setFailed(false);
    setTab(initialTab);
    load();
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
    call(crmApi.getContacts).then(setContacts, () => setContacts([]));
  }, [call, load, taskId, initialTab, me]);

  // Opened, the task is read: its comments no longer count as new.
  useEffect(() => {
    if (task?.unreadComments) call(crmApi.markTaskRead, { id: task.id }).then(notify, () => {});
  }, [call, notify, task?.id, task?.unreadComments]);

  const thread = useCommentThread({
    key: taskId,
    load: () => call(crmApi.listComments, { taskId }),
    add: (body, mentions) => call(crmApi.addComment, { taskId, body, mentions }),
    people: team,
    onCommented: notify,
  });

  const close = () => toggleSlideOver();
  const fail = (error) => {
    const message = crmTaskErrorText(error, tasksText);
    if (message) toast.error(message);
  };
  const attempt = async (run, message, { closes = false } = {}) => {
    try {
      const result = await run();
      if (message) toast.success(message);
      notify();
      if (closes) close();
      else await load();
      return result ?? true;
    } catch (error) {
      fail(error);
      return false;
    }
  };

  const save = (fields) =>
    attempt(
      () => call(crmApi.updateTask, { id: task.id, fields }),
      text('note' in fields ? 'descriptionSaved' : 'emailSaved'),
    );
  const assign = (assigneeId) => {
    const person = team.find((member) => member.id === assigneeId);
    return attempt(
      () => call(crmApi.assignTask, { id: task.id, assigneeId }),
      text('assigned', { name: `${person?.firstname ?? ''} ${person?.lastname ?? ''}`.trim() }),
    );
  };
  const toggleDone = () =>
    attempt(() => call(crmApi.setTaskDone, { id: task.id, done: !task.doneAt }), text(task.doneAt ? 'reopened' : 'done'), {
      closes: true,
    });
  const remove = async () => {
    const deleted = await attempt(() => call(crmApi.deleteTask, { id: task.id }), null, { closes: true });
    if (!deleted) return;
    toast.success(
      <UndoToast
        message={text('deleted')}
        undoLabel={text('undo')}
        onUndo={() => call(crmApi.restoreTask, { task: deleted }).then(notify, fail)}
      />,
    );
  };
  const openCompany = (company) => {
    setObjID(company.id);
    toggleSlideOver('get_company');
  };

  if (failed) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')} />
        <SlideOverBody>
          <p className="py-16 text-center text-sm text-gray-600">{text('loadError')}</p>
        </SlideOverBody>
      </>
    );
  }
  const tabs = [
    { id: 'detail', label: text('tabDetail') },
    { id: 'activity', label: text('tabActivity') },
    { id: 'comments', label: text('tabComments') },
  ];
  // The title and the tabs' content are drawn in grey until the task lands;
  // the tabs themselves are already there.
  if (!task) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')}>
          <span className="block h-7 w-2/3 animate-pulse rounded bg-gray-200" />
        </SlideOverHeader>
        <div className="flex-shrink-0 px-4 md:px-6">
          <LineTabs activeTab={tab} onChange={setTab} tabs={tabs} />
        </div>
        <SlideOverBody className="flex flex-col">
          {tab === 'detail' && <TaskDetailSkeleton />}
          {tab === 'activity' && <TaskActivitySkeleton />}
          {tab === 'comments' && <TaskCommentsSkeleton />}
        </SlideOverBody>
      </>
    );
  }

  // `access` comes from the api and is the only source of rights; a task
  // without it (the demo) keeps every gesture.
  const canEdit = task.access?.canEdit !== false;
  const canDelete = task.access?.canDelete !== false;
  const assignee = team.find((member) => member.id === task.assigneeId);

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        {task.title}
      </SlideOverHeader>
      {/* The assignee's avatar sits right of the tabs, as on the prototype. */}
      <div className="relative flex-shrink-0 px-4 md:px-6">
        <LineTabs activeTab={tab} onChange={setTab} tabs={tabs} />
        <span className="absolute -top-0.5 right-4 md:right-6">
          {canEdit ? (
            <AssignMenu value={task.assigneeId} employees={team} meId={me?.id} onChange={assign} />
          ) : (
            assignee && (
              <Tooltips text={`${assignee.firstname} ${assignee.lastname}`} placement="top">
                <span className="flex">
                  <EmployeeAvatar employee={assignee} />
                </span>
              </Tooltips>
            )
          )}
        </span>
      </div>
      <SlideOverBody className="flex flex-col">
        {tab === 'detail' && <TaskDetailTab task={task} onOpenCompany={openCompany} onSave={save} canEdit={canEdit} />}
        {tab === 'activity' && <TaskActivityTab task={task} team={team} contacts={contacts} />}
        {tab === 'comments' &&
          (thread.comments ? <CommentThread comments={thread.comments} people={team} /> : <TaskCommentsSkeleton />)}
      </SlideOverBody>
      {tab === 'comments' && (
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4">
          <CommentComposer people={team} meId={me?.id} onSend={thread.send} />
        </div>
      )}
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <div className="mr-auto flex items-center gap-2">
            {canEdit && (
              <IconTooltip label={text('edit')}>
                <button
                  type="button"
                  aria-label={text('edit')}
                  onClick={() => openTaskForm({ taskId: task.id })}
                  className={cardIconButtonClass}
                >
                  <Pencil wh={18} color="#374151" />
                </button>
              </IconTooltip>
            )}
            {canDelete && (
              <IconTooltip label={text('delete')}>
                <button type="button" aria-label={text('delete')} onClick={remove} className={cardIconButtonClass}>
                  <TrashIcon wh={18} color="#374151" />
                </button>
              </IconTooltip>
            )}
          </div>
          {canEdit && (
            <Button color={task.doneAt ? 'SECONDARY' : 'PRIMARY'} size="SM" onClick={toggleDone}>
              {text(task.doneAt ? 'reopen' : 'markDone')}
            </Button>
          )}
        </SlideOverFooter>
      </div>
    </>
  );
};

export default TaskPanel;
